import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Table,
  Button,
  Input,
  Select,
  Tag,
  Card,
  Modal,
  message,
  Popconfirm,
  Space,
  Drawer,
  Descriptions,
  InputNumber,
  Form,
  DatePicker,
  Divider,
  Row,
  Col,
  Statistic,
  Switch,
  Tooltip,
  Alert,
} from "antd";
import {
  Plus,
  Search,
  Trash2,
  Download,
  Eye,
  Send,
  CreditCard,
  FileText,
  PenLine,
  RefreshCw,
  History,
  Link2,
} from "lucide-react";
import StatusHistoryModal from "../../components/StatusHistoryModal";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import dayjs from "dayjs";
import {
  useInvoiceList,
  useInvoiceStats,
  useInvoiceCreate,
  useInvoiceUpdate,
  useInvoiceDelete,
  useInvoiceUpdateStatus,
  useInvoiceRecordPayment,
  useInvoiceFromQuote,
  useInvoiceFromPO,
  useInvoiceGenerateShareLink,
  useInvoiceCreateCreditNote,
} from "../../../hooks/api/invoices/hooks";
import {
  IInvoice,
  IInvoiceCreatePayload,
  IInvoiceLineItem,
  TInvoiceOrigin,
  TInvoiceStatus,
  TInvoiceType,
} from "../../../types/api/invoice";
import axios from "../../../lib/axios";
import { useQuoteList } from "../../../hooks/api/quotes/hooks";
import { usePOList } from "../../../hooks/api/purchase-orders/hooks";
import { IQuote } from "../../../types/api/quote";
import { useTerm } from "../../../lib/terminology";
import { useClientList } from "../../../hooks/api/clients/hooks";
import { INVOICE_STATUS_SEMANTIC, SEMANTIC_HEX, buildTagColorMap } from "../../../constants/statusColors";

// ── Constants ──────────────────────────────────────────────────────────────────

// Tag color for each invoice status is derived from the shared semantic
// palette (constants/statusColors.ts) so "paid" is the same green as a
// "completed" job or an "accepted" quote everywhere in the app.
const STATUS_COLOR: Record<TInvoiceStatus, string> = buildTagColorMap(INVOICE_STATUS_SEMANTIC);

const STATUS_LABEL: Record<TInvoiceStatus, string> = {
  draft: "Draft",
  sent: "Sent",
  partially_paid: "Partially Paid",
  paid: "Paid",
  overdue: "Overdue",
  written_off: "Written Off",
  void: "Void",
};

const STATUS_TRANSITIONS: Record<TInvoiceStatus, TInvoiceStatus[]> = {
  draft: ["sent", "void"],
  sent: ["overdue", "void"],
  partially_paid: ["overdue", "void", "written_off"],
  paid: [],
  overdue: ["void", "written_off"],
  written_off: [],
  void: [],
};

const ORIGIN_CFG: Record<TInvoiceOrigin, { label: string; cls: string }> = {
  from_quote: { label: "From Quote", cls: "bg-purple-50 text-purple-700 border-purple-200" },
  from_po: { label: "From PO", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  standalone: { label: "Standalone", cls: "bg-slate-50 text-slate-500 border-slate-200" },
};

const VAT_OPTIONS = [
  { value: 0, label: "0%" },
  { value: 5, label: "5%" },
  { value: 20, label: "20%" },
];

const TYPE_OPTIONS: { value: TInvoiceType; label: string }[] = [
  { value: "standard", label: "Standard" },
  { value: "credit_note", label: "Credit Note" },
  { value: "pro_forma", label: "Pro Forma" },
  { value: "recurring", label: "Recurring" },
];

const TABS: { label: string; key: string }[] = [
  { label: "All", key: "all" },
  { label: "Draft", key: "draft" },
  { label: "Sent", key: "sent" },
  { label: "Partially Paid", key: "partially_paid" },
  { label: "Paid", key: "paid" },
  { label: "Overdue", key: "overdue" },
  { label: "Written Off", key: "written_off" },
  { label: "Void", key: "void" },
];

// ── CSV Export ─────────────────────────────────────────────────────────────────

function exportCsv(invoices: IInvoice[]) {
  const headers = [
    "Invoice #", "Client", "Title", "Type", "Status",
    "Issue Date", "Due Date", "Subtotal", "VAT", "Total",
    "Amount Paid", "Outstanding", "PO Reference", "Quote #", "Job Ref",
  ];
  const rows = invoices.map((inv) => [
    inv.invoice_number,
    inv.client_name ?? "",
    inv.title ?? "",
    inv.invoice_type,
    inv.status,
    inv.issue_date,
    inv.due_date,
    Number(inv.subtotal).toFixed(2),
    Number(inv.vat_amount).toFixed(2),
    Number(inv.total).toFixed(2),
    Number(inv.amount_paid).toFixed(2),
    Number(inv.amount_outstanding).toFixed(2),
    inv.po_reference ?? "",
    inv.quote_number ?? "",
    inv.job_number ?? "",
  ]);
  const csv = [headers, ...rows].map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `invoices-${dayjs().format("YYYY-MM-DD")}.csv`;
  a.click();
}

const ITEM_TYPE_OPTIONS = [
  { value: "labour", label: "Labour" },
  { value: "materials", label: "Materials" },
  { value: "callout", label: "Callout Fee" },
  { value: "travel", label: "Travel" },
  { value: "other", label: "Other" },
];

// ── Line Items Editor ──────────────────────────────────────────────────────────

function LineItemsEditor({
  items,
  onChange,
}: {
  items: IInvoiceLineItem[];
  onChange: (items: IInvoiceLineItem[]) => void;
}) {
  const addRow = () =>
    onChange([
      ...items,
      { description: "", quantity: 1, unit_price: 0, vat_rate: 20, line_total: 0, item_type: "other" } as any,
    ]);

  const updateRow = (idx: number, field: string, value: any) => {
    const updated = items.map((item, i) => {
      if (i !== idx) return item;
      const next = { ...item, [field]: value } as any;
      next.line_total =
        Math.round(Number(next.quantity) * Number(next.unit_price) * 100) / 100;
      return next;
    });
    onChange(updated);
  };

  return (
    <div>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-slate-50 text-xs text-slate-500 font-medium">
            <th className="text-left px-2 py-1.5 border border-slate-200 w-[28%]">Description</th>
            <th className="text-left px-2 py-1.5 border border-slate-200 w-[14%]">Type</th>
            <th className="text-left px-2 py-1.5 border border-slate-200 w-[10%]">Qty</th>
            <th className="text-left px-2 py-1.5 border border-slate-200 w-[14%]">Unit Price</th>
            <th className="text-left px-2 py-1.5 border border-slate-200 w-[12%]">VAT</th>
            <th className="text-left px-2 py-1.5 border border-slate-200 w-[12%]">Total</th>
            <th className="border border-slate-200 w-[10%]" />
          </tr>
        </thead>
        <tbody>
          {items.map((item: any, idx) => (
            <tr key={idx}>
              <td className="px-1 py-1 border border-slate-200">
                <Input
                  size="small"
                  value={item.description}
                  onChange={(e) => updateRow(idx, "description", e.target.value)}
                  placeholder="Description"
                />
              </td>
              <td className="px-1 py-1 border border-slate-200">
                <Select
                  size="small"
                  value={item.item_type || "other"}
                  options={ITEM_TYPE_OPTIONS}
                  onChange={(v) => updateRow(idx, "item_type", v)}
                  className="w-full"
                />
              </td>
              <td className="px-1 py-1 border border-slate-200">
                <InputNumber
                  size="small"
                  min={0.01}
                  step={0.01}
                  value={item.quantity}
                  onChange={(v) => updateRow(idx, "quantity", v ?? 1)}
                  className="w-full"
                />
              </td>
              <td className="px-1 py-1 border border-slate-200">
                <InputNumber
                  size="small"
                  min={0}
                  step={0.01}
                  prefix="£"
                  value={item.unit_price}
                  onChange={(v) => updateRow(idx, "unit_price", v ?? 0)}
                  className="w-full"
                />
              </td>
              <td className="px-1 py-1 border border-slate-200">
                <Select
                  size="small"
                  value={item.vat_rate}
                  options={VAT_OPTIONS}
                  onChange={(v) => updateRow(idx, "vat_rate", v)}
                  className="w-full"
                />
              </td>
              <td className="px-1 py-1 border border-slate-200 text-right text-slate-600 font-medium">
                £{Number(item.line_total).toFixed(2)}
              </td>
              <td className="px-1 py-1 border border-slate-200 text-center">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<Trash2 size={12} />}
                  onClick={() => onChange(items.filter((_, i) => i !== idx))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button
        size="small"
        type="dashed"
        icon={<Plus size={12} />}
        onClick={addRow}
        className="mt-2"
      >
        Add Line Item
      </Button>
    </div>
  );
}

// ── Totals Preview ─────────────────────────────────────────────────────────────

function TotalsPreview({
  items,
  vatRate,
  discount,
}: {
  items: IInvoiceLineItem[];
  vatRate: number;
  discount: number;
}) {
  const subtotal = items.reduce(
    (sum, i) => sum + Number(i.quantity) * Number(i.unit_price),
    0,
  );
  const afterDiscount = Math.max(0, subtotal - Number(discount));
  const discountFactor = subtotal > 0 ? afterDiscount / subtotal : 1;
  const vatAmount = items.reduce((sum, i) => {
    const itemVat = Number(i.vat_rate ?? vatRate);
    const itemBase = Number(i.quantity) * Number(i.unit_price) * discountFactor;
    return sum + (itemBase * itemVat) / 100;
  }, 0);
  const total = afterDiscount + vatAmount;

  return (
    <div className="mt-3 bg-slate-50 rounded-lg p-3 text-sm">
      <div className="flex justify-between text-slate-500 mb-1">
        <span>Subtotal</span>
        <span>£{subtotal.toFixed(2)}</span>
      </div>
      {Number(discount) > 0 && (
        <div className="flex justify-between text-red-500 mb-1">
          <span>Discount</span>
          <span>-£{Number(discount).toFixed(2)}</span>
        </div>
      )}
      <div className="flex justify-between text-slate-500 mb-1">
        <span>VAT ({vatRate}%)</span>
        <span>£{vatAmount.toFixed(2)}</span>
      </div>
      <Divider className="my-1" />
      <div className="flex justify-between font-semibold text-slate-800">
        <span>Total</span>
        <span>£{total.toFixed(2)}</span>
      </div>
    </div>
  );
}

// ── Invoice Form Modal ─────────────────────────────────────────────────────────

function InvoiceFormModal({
  open,
  editInvoice,
  onClose,
  onSuccess,
}: {
  open: boolean;
  editInvoice?: IInvoice | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const term = useTerm();
  const [form] = Form.useForm();
  const [lineItems, setLineItems] = useState<IInvoiceLineItem[]>([
    { description: "", quantity: 1, unit_price: 0, vat_rate: 20, line_total: 0 },
  ]);
  const [vatRate, setVatRate] = useState<number>(20);
  const [discount, setDiscount] = useState<number>(0);
  const [messageApi, ctx] = message.useMessage();
  const [clientPaymentDays, setClientPaymentDays] = useState<number>(30);
  const isEdit = !!editInvoice;

  const { data: clientRes } = useClientList({ page: 1, limit: 200 });
  const clientOptions = ((clientRes?.data as any[]) ?? []).map((c) => {
    const name = c.business_name || `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || "—";
    const sub = c.email || c.phone || c.mobile || "";
    return { value: c.id, label: sub ? `${name} (${sub})` : name, paymentTermsDays: c.payment_terms_days };
  });

  function handleClientChange(clientId: number | null) {
    const client = (clientRes?.data as any[] ?? []).find((c: any) => c.id === clientId);
    const days = client?.payment_terms_days ? Number(client.payment_terms_days) : 30;
    setClientPaymentDays(days);
    const issueDate = form.getFieldValue("issue_date");
    if (issueDate) {
      form.setFieldsValue({ due_date: dayjs(issueDate).add(days, "day") });
    }
  }

  function handleIssueDateChange(date: any) {
    if (date && !isEdit) {
      form.setFieldsValue({ due_date: dayjs(date).add(clientPaymentDays, "day") });
    }
  }

  const { mutate: create, isPending: creating } = useInvoiceCreate({
    onSuccess: () => {
      messageApi.success("Invoice created.");
      onSuccess();
    },
    onError: (e: any) =>
      messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });

  const { mutate: update, isPending: updating } = useInvoiceUpdate({
    onSuccess: () => {
      messageApi.success("Invoice updated.");
      onSuccess();
    },
    onError: (e: any) =>
      messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });

  const handleAfterOpen = useCallback(() => {
    if (editInvoice) {
      form.setFieldsValue({
        title: editInvoice.title,
        invoice_type: editInvoice.invoice_type,
        issue_date: editInvoice.issue_date ? dayjs(editInvoice.issue_date) : null,
        due_date: editInvoice.due_date ? dayjs(editInvoice.due_date) : null,
        vat_rate: editInvoice.vat_rate,
        discount_amount: editInvoice.discount_amount,
        po_reference: editInvoice.po_reference,
        notes: editInvoice.notes,
        terms: editInvoice.terms,
        internal_notes: editInvoice.internal_notes,
      });
      setLineItems(
        editInvoice.line_items?.length
          ? editInvoice.line_items.map((li) => ({
              description: li.description,
              quantity: Number(li.quantity),
              unit_price: Number(li.unit_price),
              vat_rate: Number(li.vat_rate),
              line_total: Number(li.line_total),
            }))
          : [{ description: "", quantity: 1, unit_price: 0, vat_rate: 20, line_total: 0 }],
      );
      setVatRate(Number(editInvoice.vat_rate));
      setDiscount(Number(editInvoice.discount_amount));
    } else {
      form.resetFields();
      setClientPaymentDays(30);
      form.setFieldsValue({
        invoice_type: "standard",
        vat_rate: 20,
        discount_amount: 0,
        issue_date: dayjs(),
        due_date: dayjs().add(30, "day"),
      });
      setLineItems([
        { description: "", quantity: 1, unit_price: 0, vat_rate: 20, line_total: 0 },
      ]);
      setVatRate(20);
      setDiscount(0);
    }
  }, [editInvoice, form]);

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      if (!lineItems.length || lineItems.some((li) => !li.description?.trim())) {
        messageApi.error("All line items must have a description.");
        return;
      }
      const payload: IInvoiceCreatePayload = {
        client_id: values.client_id ?? null,
        title: values.title ?? null,
        invoice_type: values.invoice_type ?? "standard",
        issue_date: values.issue_date?.format("YYYY-MM-DD"),
        due_date: values.due_date?.format("YYYY-MM-DD"),
        vat_rate: values.vat_rate ?? 20,
        discount_amount: values.discount_amount ?? 0,
        po_reference: values.po_reference ?? null,
        notes: values.notes ?? null,
        terms: values.terms ?? null,
        internal_notes: values.internal_notes ?? null,
        line_items: lineItems,
      };
      isEdit && editInvoice ? update({ ...payload, id: editInvoice.id }) : create(payload);
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      afterOpenChange={(v) => v && handleAfterOpen()}
      title={isEdit ? `Edit ${term("invoice")} — ${editInvoice?.invoice_number}` : `New ${term("invoice")}`}
      width="min(900px, 95vw)"
      footer={null}
      destroyOnClose
    >
      {ctx}
      <Form form={form} layout="vertical" size="small">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Client" name="client_id">
              <Select
                showSearch
                allowClear
                placeholder="Select client…"
                options={clientOptions}
                filterOption={(input, opt) =>
                  ((opt?.label as string) ?? "").toLowerCase().includes(input.toLowerCase())
                }
                onChange={handleClientChange}
                disabled={isEdit}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Invoice Type" name="invoice_type">
              <Select options={TYPE_OPTIONS} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Title / Reference" name="title" className="mb-3">
          <Input placeholder="e.g. Web Design Phase 1" />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Issue Date"
              name="issue_date"
              rules={[{ required: true, message: "Required" }]}
            >
              <DatePicker className="w-full" format="DD/MM/YYYY" onChange={handleIssueDateChange} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Due Date"
              name="due_date"
            >
              <DatePicker className="w-full" format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="VAT Rate" name="vat_rate">
              <InputNumber
                min={0}
                max={100}
                addonAfter="%"
                className="w-full"
                onChange={(v) => setVatRate(Number(v ?? 20))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Discount" name="discount_amount">
              <InputNumber
                min={0}
                step={0.01}
                addonBefore="£"
                className="w-full"
                onChange={(v) => setDiscount(Number(v ?? 0))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="PO Reference" name="po_reference">
              <Input placeholder="Customer PO number" />
            </Form.Item>
          </Col>
        </Row>

        <Divider className="my-3" />
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
          Line Items
        </div>
        <LineItemsEditor items={lineItems} onChange={setLineItems} />
        <TotalsPreview items={lineItems} vatRate={vatRate} discount={discount} />

        <Divider className="my-3" />
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
          Notes
        </div>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Notes to Client" name="notes">
              <Input.TextArea rows={3} placeholder="Visible on invoice" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Terms & Conditions" name="terms">
              <Input.TextArea rows={3} placeholder="Payment terms, etc." />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Internal Notes" name="internal_notes">
          <Input.TextArea rows={2} placeholder="Not visible to client" />
        </Form.Item>

        <div className="flex justify-end gap-2 mt-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={creating || updating}
            onClick={handleSubmit}
          >
            {isEdit ? "Update Invoice" : "Create Invoice"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

// ── Create From Quote Modal ────────────────────────────────────────────────────
function FromQuoteModal({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (inv: IInvoice) => void;
}) {
  const [form] = Form.useForm();
  const [messageApi, ctx] = message.useMessage();
const ACCEPTED_QUOTE_Q = { page: 1, limit: 200, status: "accepted" };

  const { data: acceptedQuoteRes } = useQuoteList(ACCEPTED_QUOTE_Q);

  const acceptedQuotes: IQuote[] =
    (acceptedQuoteRes?.data as IQuote[] | undefined) ?? [];

  const quotationOptions = acceptedQuotes.map((q) => ({
    value: q.id,
    label: `${q.quote_number}${q.title ? ` — ${q.title}` : ""}${
      q.client_name ? ` (${q.client_name})` : ""
    }`,
    data: q,
  }));

  const handleQuotationSelect = (value: number, option: any) => {
    const quote = option?.data as IQuote | undefined;

    if (!quote) return;

    // optional: auto-fill form fields from quote
    form.setFieldsValue({
      title: quote.title,
      notes: quote.notes,
    });
  };

  const { mutate: fromQuote, isPending } = useInvoiceFromQuote({
    onSuccess: (inv) => {
      messageApi.success("Invoice created from quote.");
      form.resetFields();
      onSuccess(inv);
    },
    onError: (e: any) =>
      messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Create Invoice from Quote"
      destroyOnClose
      confirmLoading={isPending}
      okText="Create Invoice"
      okButtonProps={{}}
      onOk={() =>
        form.validateFields().then((values) =>
          fromQuote({
            quote_id: Number(values.quote_id),
            title: values.title ?? null,
            issue_date: values.issue_date?.format("YYYY-MM-DD"),
            due_date: values.due_date?.format("YYYY-MM-DD"),
            po_reference: values.po_reference ?? null,
            notes: values.notes ?? null,
            terms: values.terms ?? null,
          }),
        )
      }
      afterOpenChange={(v) => {
        if (v) {
          form.resetFields();
          form.setFieldsValue({
            issue_date: dayjs(),
            due_date: dayjs().add(30, "day"),
          });
        }
      }}
    >
      {ctx}

      <Form form={form} layout="vertical" size="small" className="mt-2">
        <Form.Item
          name="quote_id"
          label={
            <span>
              Source Quotation{" "}
              <span className="text-xs text-amber-600 font-normal">
                — only accepted quotations are listed
              </span>
            </span>
          }
          rules={[{ required: true, message: "Quotation is required" }]}
        >
          <Select
            showSearch
            placeholder="Select an accepted quotation…"
            options={quotationOptions}
            onChange={handleQuotationSelect}
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            notFoundContent={
              <span className="text-xs text-slate-400 p-2 block">
                No accepted quotations available. Ask clients to accept a quote first.
              </span>
            }
          />
        </Form.Item>

        <Form.Item label="Title Override" name="title">
          <Input placeholder="Leave blank to use quote title" />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Issue Date" name="issue_date">
              <DatePicker className="w-full" format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Due Date" name="due_date">
              <DatePicker className="w-full" format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="PO Reference" name="po_reference">
          <Input placeholder="Optional" />
        </Form.Item>

        <Form.Item label="Notes Override" name="notes">
          <Input.TextArea
            rows={2}
            placeholder="Leave blank to copy from quote"
          />
        </Form.Item>

        <p className="text-xs text-slate-400">
          Line items, client, VAT rate, and discount are copied automatically from the quote.
        </p>
      </Form>
    </Modal>
  );
}

// ── Create From PO Modal ───────────────────────────────────────────────────────
function FromPOModal({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (inv: IInvoice) => void;
}) {
  const [form] = Form.useForm();
  const [messageApi, ctx] = message.useMessage();
  const { data: poRes } = usePOList({ page: 1, limit: 200, status: "fulfilled" });
  const pos = (poRes?.data as any[] | undefined) ?? [];
  const poOptions = pos.map((p: any) => ({
    value: p.id,
    label: `${p.po_number}${p.supplier_name ? ` — ${p.supplier_name}` : ""}`,
  }));

  const { mutate: fromPO, isPending } = useInvoiceFromPO({
    onSuccess: (inv) => {
      messageApi.success("Invoice created from Purchase Order.");
      form.resetFields();
      onSuccess(inv);
    },
    onError: (e: any) =>
      messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Create Invoice from Purchase Order"
      destroyOnClose
      confirmLoading={isPending}
      okText="Create Invoice"
      okButtonProps={{}}
      onOk={() =>
        form.validateFields().then((values) =>
          fromPO({
            po_id: Number(values.po_id),
            title: values.title ?? undefined,
          }),
        )
      }
      afterOpenChange={(v) => { if (v) form.resetFields(); }}
    >
      {ctx}
      <Form form={form} layout="vertical" size="small" className="mt-2">
        <Form.Item name="po_id" label="Source Purchase Order" rules={[{ required: true, message: "Purchase Order is required" }]}>
          <Select showSearch placeholder="Select a fulfilled PO…" options={poOptions} filterOption={(input, opt) => ((opt?.label as string) ?? "").toLowerCase().includes(input.toLowerCase())} />
        </Form.Item>
        <Form.Item name="title" label="Invoice Title (optional)">
          <Input placeholder="Leave blank to use PO supplier name" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Payment Modal ──────────────────────────────────────────────────────────────

function PaymentModal({
  invoice,
  onClose,
  onSuccess,
}: {
  invoice: IInvoice | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [amount, setAmount] = useState<number>(0);
  const [paidAt, setPaidAt] = useState<dayjs.Dayjs | null>(dayjs());
  const [messageApi, ctx] = message.useMessage();

  const { mutate: recordPayment, isPending } = useInvoiceRecordPayment({
    onSuccess: () => { messageApi.success("Payment recorded."); onSuccess(); },
    onError: (e: any) =>
      messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });

  if (!invoice) return null;
  const outstanding = Number(invoice.amount_outstanding);

  return (
    <Modal
      open={!!invoice}
      onCancel={onClose}
      title={`Record Payment — ${invoice.invoice_number}`}
      onOk={() => {
        if (!amount || amount <= 0) { messageApi.error("Enter a valid amount."); return; }
        recordPayment({ id: invoice.id, payment_amount: amount, paid_at: paidAt?.toISOString() ?? null });
      }}
      okText="Record Payment"
      confirmLoading={isPending}
      okButtonProps={{ style: { background: SEMANTIC_HEX.success, borderColor: SEMANTIC_HEX.success } }}
      afterOpenChange={(v) => { if (v) setAmount(outstanding); }}
      destroyOnClose
    >
      {ctx}
      <div className="space-y-4 mt-2">
        <div className="bg-slate-50 rounded-lg p-3 text-sm">
          <div className="flex justify-between text-slate-500">
            <span>Invoice Total</span>
            <span className="font-medium">£{Number(invoice.total).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-slate-500 mt-1">
            <span>Already Paid</span>
            <span className="text-emerald-600 font-medium">£{Number(invoice.amount_paid).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-slate-700 font-semibold mt-1">
            <span>Outstanding</span>
            <span className="text-red-600">£{outstanding.toFixed(2)}</span>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Payment Amount (£)</label>
          <InputNumber
            value={amount}
            onChange={(v) => setAmount(Number(v ?? 0))}
            min={0.01}
            max={outstanding}
            step={0.01}
            prefix="£"
            className="w-full"
          />
          <p className="text-xs text-slate-400 mt-1">
            Max: £{outstanding.toFixed(2)}.
          </p>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Payment Date</label>
          <DatePicker
            value={paidAt}
            onChange={(d) => setPaidAt(d)}
            format="DD/MM/YYYY"
            className="w-full"
          />
        </div>
      </div>
    </Modal>
  );
}

// ── Status Modal ──────────────────────────────────────────────────────────────

function StatusModal({
  invoice,
  onClose,
  onSuccess,
}: {
  invoice: IInvoice | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [targetStatus, setTargetStatus] = useState<TInvoiceStatus | null>(null);
  const [messageApi, ctx] = message.useMessage();

  const { mutate: updateStatus, isPending } = useInvoiceUpdateStatus({
    onSuccess: () => { messageApi.success("Status updated."); onSuccess(); },
    onError: (e: any) =>
      messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });

  if (!invoice) return null;
  const allowed = STATUS_TRANSITIONS[invoice.status] ?? [];

  return (
    <Modal
      open={!!invoice}
      onCancel={() => { setTargetStatus(null); onClose(); }}
      title={`Update Status — ${invoice.invoice_number}`}
      onOk={() => {
        if (!targetStatus) { messageApi.error("Select a target status."); return; }
        updateStatus({ id: invoice.id, status: targetStatus });
      }}
      okText="Update Status"
      confirmLoading={isPending}
      okButtonProps={{}}
      afterOpenChange={(v) => { if (v) setTargetStatus(null); }}
      destroyOnClose
    >
      {ctx}
      <p className="text-slate-500 text-sm mb-3">
        Current: <Tag color={STATUS_COLOR[invoice.status]}>{STATUS_LABEL[invoice.status]}</Tag>
      </p>
      <div className="space-y-2">
        {allowed.length === 0 ? (
          <p className="text-slate-400 text-sm">This status is terminal — no further transitions.</p>
        ) : (
          allowed.map((s) => (
            <button
              key={s}
              onClick={() => setTargetStatus(s)}
              className={`w-full text-left px-3 py-2 rounded-lg border transition-colors text-sm ${
                targetStatus === s
                  ? "border-blue-500 bg-blue-50 font-medium"
                  : "border-slate-200 hover:bg-slate-50"
              }`}
            >
              <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s]}</Tag>
              {s === "void" && <span className="text-slate-400 text-xs ml-2">Cancels the invoice</span>}
              {s === "written_off" && <span className="text-slate-400 text-xs ml-2">Marks as unrecoverable</span>}
              {s === "sent" && <span className="text-slate-400 text-xs ml-2">Mark as sent to client</span>}
            </button>
          ))
        )}
      </div>
    </Modal>
  );
}

// ── Detail Drawer ─────────────────────────────────────────────────────────────

function InvoiceDetailDrawer({
  invoice,
  onClose,
  onDownloadPdf,
}: {
  invoice: IInvoice | null;
  onClose: () => void;
  onDownloadPdf: (inv: IInvoice) => void;
}) {
  const fmt = (n: number | null | undefined) => `£${Number(n ?? 0).toFixed(2)}`;
  if (!invoice) return null;

  return (
    <Drawer
      open={!!invoice}
      onClose={onClose}
      title={`Invoice ${invoice.invoice_number}`}
      width="min(560px, 98vw)"
    >
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <Tag color={STATUS_COLOR[invoice.status]} className="text-sm px-3 py-1">
            {STATUS_LABEL[invoice.status]}
          </Tag>
          <Button
            size="small"
            icon={<Download size={13} />}
            onClick={() => onDownloadPdf(invoice)}
          >
            Download PDF
          </Button>
        </div>

        <div>
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Details</div>
          <Descriptions column={1} bordered size="small" labelStyle={{ fontWeight: 600, width: 140 }}>
            <Descriptions.Item label="Invoice #">{invoice.invoice_number}</Descriptions.Item>
            {invoice.title && <Descriptions.Item label="Title">{invoice.title}</Descriptions.Item>}
            <Descriptions.Item label="Type">
              <Tag>{invoice.invoice_type.replace(/_/g, " ")}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Client">{invoice.client_name || "—"}</Descriptions.Item>
            <Descriptions.Item label="Origin">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${ORIGIN_CFG[invoice.invoice_origin]?.cls ?? ""}`}>
                {ORIGIN_CFG[invoice.invoice_origin]?.label ?? invoice.invoice_origin}
              </span>
            </Descriptions.Item>
            {invoice.quote_number && <Descriptions.Item label="Quote">{invoice.quote_number}</Descriptions.Item>}
            {invoice.job_number && <Descriptions.Item label="Job">{invoice.job_number}</Descriptions.Item>}
            {invoice.po_reference && <Descriptions.Item label="PO Ref">{invoice.po_reference}</Descriptions.Item>}
            <Descriptions.Item label="Issue Date">{dayjs(invoice.issue_date).format("DD MMM YYYY")}</Descriptions.Item>
            <Descriptions.Item label="Due Date">
              <span className={
                dayjs(invoice.due_date).isBefore(dayjs(), "day") &&
                !["void", "written_off"].includes(invoice.status)
                  ? "text-red-600 font-medium"
                  : ""
              }>
                {dayjs(invoice.due_date).format("DD MMM YYYY")}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Created">{dayjs(invoice.created_at).format("DD MMM YYYY HH:mm")}</Descriptions.Item>
          </Descriptions>
        </div>

        <div>
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Financials</div>
          <Descriptions column={1} bordered size="small" labelStyle={{ fontWeight: 600, width: 140 }}>
            <Descriptions.Item label="Subtotal">{fmt(invoice.subtotal)}</Descriptions.Item>
            {Number(invoice.discount_amount) > 0 && (
              <Descriptions.Item label="Discount">-{fmt(invoice.discount_amount)}</Descriptions.Item>
            )}
            <Descriptions.Item label={`VAT (${invoice.vat_rate}%)`}>{fmt(invoice.vat_amount)}</Descriptions.Item>
            <Descriptions.Item label="Total"><span className="font-semibold">{fmt(invoice.total)}</span></Descriptions.Item>
            <Descriptions.Item label="Amount Paid">
              <span className="text-emerald-600 font-medium">{fmt(invoice.amount_paid)}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Outstanding">
              <span className={Number(invoice.amount_outstanding) > 0 ? "text-red-600 font-semibold" : "text-emerald-600 font-semibold"}>
                {fmt(invoice.amount_outstanding)}
              </span>
            </Descriptions.Item>
          </Descriptions>
        </div>

        {(invoice.line_items?.length ?? 0) > 0 && (
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Line Items</div>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50 text-slate-500">
                  <th className="text-left px-2 py-1.5 border border-slate-100">Description</th>
                  <th className="text-right px-2 py-1.5 border border-slate-100">Qty</th>
                  <th className="text-right px-2 py-1.5 border border-slate-100">Price</th>
                  <th className="text-right px-2 py-1.5 border border-slate-100">VAT</th>
                  <th className="text-right px-2 py-1.5 border border-slate-100">Total</th>
                </tr>
              </thead>
              <tbody>
                {invoice.line_items!.map((li, i) => (
                  <tr key={i} className="border-t border-slate-50">
                    <td className="px-2 py-1 border border-slate-100">{li.description}</td>
                    <td className="px-2 py-1 border border-slate-100 text-right">{Number(li.quantity).toFixed(2)}</td>
                    <td className="px-2 py-1 border border-slate-100 text-right">£{Number(li.unit_price).toFixed(2)}</td>
                    <td className="px-2 py-1 border border-slate-100 text-right">{li.vat_rate}%</td>
                    <td className="px-2 py-1 border border-slate-100 text-right font-medium">£{Number(li.line_total).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(invoice.notes || invoice.terms) && (
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Notes</div>
            {invoice.notes && <p className="text-xs text-slate-600 mb-2">{invoice.notes}</p>}
            {invoice.terms && <p className="text-xs text-slate-500">{invoice.terms}</p>}
          </div>
        )}
      </div>
    </Drawer>
  );
}

// ── Credit Note Modal ──────────────────────────────────────────────────────────

function CreditNoteModal({
  invoice,
  onClose,
  onSuccess,
}: {
  invoice: IInvoice | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [reason, setReason] = useState("");
  const [messageApi, ctx] = message.useMessage();

  const { mutate: createCreditNote, isPending } = useInvoiceCreateCreditNote({
    onSuccess: () => { messageApi.success("Credit note created."); onSuccess(); },
    onError: (e: any) =>
      messageApi.error(e?.response?.data?.message || e?.message || "Failed to create credit note."),
  });

  if (!invoice) return null;

  return (
    <Modal
      open={!!invoice}
      onCancel={onClose}
      title={`Create Credit Note — ${invoice.invoice_number}`}
      onOk={() => {
        createCreditNote({ original_invoice_id: invoice.id, reason: reason || undefined });
      }}
      okText="Create Credit Note"
      confirmLoading={isPending}
      okButtonProps={{ style: { background: SEMANTIC_HEX.info, borderColor: SEMANTIC_HEX.info } }}
      afterOpenChange={(v) => { if (v) setReason(""); }}
      destroyOnClose
    >
      {ctx}
      <div className="space-y-4 mt-2">
        <div className="bg-slate-50 rounded-lg p-3 text-sm">
          <div className="flex justify-between text-slate-500">
            <span>Original Invoice</span>
            <span className="font-medium">{invoice.invoice_number}</span>
          </div>
          <div className="flex justify-between text-slate-500 mt-1">
            <span>Client</span>
            <span>{invoice.client_name || "—"}</span>
          </div>
          <div className="flex justify-between text-slate-700 font-semibold mt-1">
            <span>Invoice Total</span>
            <span>£{Number(invoice.total).toFixed(2)}</span>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Reason (optional)</label>
          <Input.TextArea
            rows={3}
            placeholder="e.g. Returned goods, billing error..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <p className="text-xs text-slate-400">
          A new credit note invoice will be created with negated line items from the original invoice.
        </p>
      </div>
    </Modal>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const InvoicesPage = () => {
  const term = useTerm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("status") || "all");

  // Deep-link from Dashboard cells: /invoices?status=overdue
  useEffect(() => {
    const statusParam = searchParams.get("status");
    if (statusParam) {
      setSearchParams((prev) => {
        prev.delete("status");
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [search, setSearch] = useState("");
const [debouncedSearch, setDebouncedSearch] = useState("");
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState<string>("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [page, setPage] = useState(1);

  const [showCreate, setShowCreate] = useState(false);
  const [showFromQuote, setShowFromQuote] = useState(false);
  const [showFromPO, setShowFromPO] = useState(false);
  const [editInvoice, setEditInvoice] = useState<IInvoice | null>(null);
  const [detailInvoice, setDetailInvoice] = useState<IInvoice | null>(null);
  const [paymentInvoice, setPaymentInvoice] = useState<IInvoice | null>(null);
  const [statusInvoice, setStatusInvoice] = useState<IInvoice | null>(null);
  const [historyInvoice, setHistoryInvoice] = useState<IInvoice | null>(null);
  const [creditNoteInvoice, setCreditNoteInvoice] = useState<IInvoice | null>(null);
  const [messageApi, ctx] = message.useMessage();

  const queryData = useMemo(() => {
  const q: any = {
    page,
    limit: 20,
  };

  if (activeTab !== "all") {
    q.status = activeTab;
  }

  if (invoiceTypeFilter) {
    q.invoice_type = invoiceTypeFilter;
  }

  if (overdueOnly) {
    q.overdue = true;
  }

  if (debouncedSearch.trim()) {
    q.search = debouncedSearch.trim();
  }

  return q;
}, [
  page,
  activeTab,
  invoiceTypeFilter,
  overdueOnly,
  debouncedSearch,
]);

  // Hook initialized once — use refetchWithQuery to drive all re-fetches
const {
  data: listData,
  isLoading,
  isError,
  error,
  refetchWithQuery,
} = useInvoiceList(queryData);

  const { data: statsData, refetch: refetchStats } = useInvoiceStats();

  // Sync filters → hook internal state
useEffect(() => {
  refetchWithQuery(queryData);
}, [queryData]);

  useEffect(() => {
  const timer = setTimeout(() => {
    setDebouncedSearch(search);
  }, 500);

  return () => clearTimeout(timer);
}, [search]);

  const invoices: IInvoice[] = listData?.data ?? [];
  const total = Number(listData?.extra?.total ?? 0);
  const stats = statsData?.data;

  const { mutate: deleteInvoice } = useInvoiceDelete({
    onSuccess: () => { messageApi.success("Invoice deleted."); refetchStats(); },
    onError: () => messageApi.error("Failed to delete."),
  });

  const { mutate: generateShareLink } = useInvoiceGenerateShareLink({
    onSuccess: (token) => {
      const url = `${window.location.origin}/invoice/${token}`;
      navigator.clipboard.writeText(url).then(
        () => messageApi.success("Share link copied to clipboard!"),
        () => messageApi.info(`Share link: ${url}`),
      );
    },
    onError: () => messageApi.error("Failed to generate share link."),
  });

  const handleDownloadPdf = async (invoice: IInvoice) => {
    try {
      const res = await axios.get(`/invoices/pdf/${invoice.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoice.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      messageApi.error("Failed to download PDF.");
    }
  };

  // Summary totals from current list data
  const totalInvoicedVal = invoices.reduce((s, i) => s + Number(i.total), 0);
  const totalCollected = invoices.reduce((s, i) => s + Number(i.amount_paid), 0);
  const totalOutstanding = invoices
    .filter((i) => !["void", "written_off", "draft"].includes(i.status))
    .reduce((s, i) => s + Number(i.amount_outstanding), 0);
  const totalOverdue = invoices
    .filter((i) => i.status === "overdue")
    .reduce((s, i) => s + Number(i.amount_outstanding), 0);

  const columns = [
    {
      title: "Invoice",
      key: "invoice",
      render: (_: any, row: IInvoice) => (
        <button
          className="text-left hover:text-blue-700 transition-colors"
          onClick={() => setDetailInvoice(row)}
        >
          <div className="font-medium text-slate-800 text-sm flex items-center gap-1">
            {row.invoice_number} <Eye size={11} className="text-slate-400" />
          </div>
          {row.po_reference && (
            <div className="text-xs text-slate-400">PO: {row.po_reference}</div>
          )}
        </button>
      ),
    },
    {
      title: "Client",
      key: "client",
      render: (_: any, row: IInvoice) => {
        const origin = ORIGIN_CFG[row.invoice_origin] ?? ORIGIN_CFG.standalone;
        return (
          <div>
            <div className="text-sm text-slate-700">{row.client_name || "—"}</div>
            {row.title && (
              <div className="text-xs text-slate-400 truncate max-w-[140px]">{row.title}</div>
            )}
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border mt-0.5 ${origin.cls}`}>
              {origin.label}
            </span>
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "invoice_type",
      key: "invoice_type",
      render: (t: TInvoiceType) => (
        <Tag className="text-xs">{t.replace(/_/g, " ")}</Tag>
      ),
    },
    {
      title: "Issued",
      dataIndex: "issue_date",
      key: "issue_date",
      render: (d: string) => (
        <span className="text-sm text-slate-500">{dayjs(d).format("DD MMM YY")}</span>
      ),
    },
    {
      title: "Due",
      dataIndex: "due_date",
      key: "due_date",
      render: (d: string, row: IInvoice) => {
        const overdue =
          dayjs(d).isBefore(dayjs(), "day") &&
          !["void", "written_off"].includes(row.status);
        return (
          <span className={`text-sm font-medium ${overdue ? "text-red-600" : "text-slate-500"}`}>
            {dayjs(d).format("DD MMM YY")}
          </span>
        );
      },
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      render: (v: number) => (
        <span className="text-sm font-semibold text-slate-700">£{Number(v).toFixed(2)}</span>
      ),
    },
    {
      title: "Outstanding",
      dataIndex: "amount_outstanding",
      key: "amount_outstanding",
      render: (v: number, row: IInvoice) => (
        <span className={`text-sm font-semibold ${
          Number(v) > 0 && !["void", "written_off"].includes(row.status)
            ? "text-red-600"
            : "text-emerald-600"
        }`}>
          £{Number(v).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: TInvoiceStatus) => (
        <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s]}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 190,
      render: (_: any, row: IInvoice) => {
        const canEdit = row.status === "draft";
        const canDelete = row.status === "draft" || row.status === "void";
        const canPay = !["draft", "void", "written_off"].includes(row.status);
        const canChangeStatus = !["void", "written_off"].includes(row.status);
        const canCreditNote = ["paid", "partially_paid", "sent", "overdue"].includes(row.status) && row.invoice_type !== "credit_note";

        return (
          <Space size={2} wrap>
            {canEdit && (
              <Tooltip title="Edit">
                <Button
                  size="small"
                  icon={<PenLine size={11} />}
                  onClick={() => setEditInvoice(row)}
                />
              </Tooltip>
            )}
            {canPay && (
              <Tooltip title="Record Payment">
                <Button
                  size="small"
                  icon={<CreditCard size={11} />}
                  onClick={() => setPaymentInvoice(row)}
                />
              </Tooltip>
            )}
            {canChangeStatus && (
              <Tooltip title="Change Status">
                <Button
                  size="small"
                  icon={<Send size={11} />}
                  onClick={() => setStatusInvoice(row)}
                />
              </Tooltip>
            )}
            <Tooltip title="Status History">
              <Button
                size="small"
                icon={<History size={11} />}
                onClick={() => setHistoryInvoice(row)}
              />
            </Tooltip>
            <Tooltip title="Download PDF">
              <Button
                size="small"
                icon={<Download size={11} />}
                onClick={() => handleDownloadPdf(row)}
              />
            </Tooltip>
            <Tooltip title="Copy Share Link">
              <Button
                size="small"
                icon={<Link2 size={11} />}
                onClick={() => generateShareLink(row.id)}
              />
            </Tooltip>
            {canCreditNote && (
              <Tooltip title="Create Credit Note">
                <Button
                  size="small"
                  icon={<FileText size={11} />}
                  onClick={() => setCreditNoteInvoice(row)}
                  style={{ color: SEMANTIC_HEX.info, borderColor: SEMANTIC_HEX.info }}
                />
              </Tooltip>
            )}
            {canDelete && (
              <Popconfirm
                title="Delete this invoice? Cannot be undone."
                onConfirm={() => deleteInvoice({ id: row.id })}
              >
                <Tooltip title="Delete">
                  <Button size="small" icon={<Trash2 size={11} />} danger type="text" />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      {ctx}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">{term("invoice", true)}</h2>
          <p className="text-sm text-slate-500 mt-0.5">Create, send and track invoices and payments</p>
        </div>
        <Space>
          <Button
            icon={<Download size={14} />}
            onClick={() => exportCsv(invoices)}
            disabled={invoices.length === 0}
          >
            Export CSV
          </Button>
          <Button
            icon={<FileText size={14} />}
            onClick={() => setShowFromQuote(true)}
          >
            From Quote
          </Button>
          <Button
            icon={<FileText size={14} />}
            onClick={() => setShowFromPO(true)}
          >
            From PO
          </Button>
          <ButtonWrapper
            type="primary"
            icon={<Plus size={14} />}
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2"
          >
            New {term("invoice")}
          </ButtonWrapper>
        </Space>
      </div>

      {/* Summary Cards */}
      <Row gutter={16} className="mb-6">
        {[
          { title: "Total Invoiced", value: totalInvoicedVal, color: SEMANTIC_HEX.neutral, sub: "Current view" },
          { title: "Collected", value: totalCollected, color: SEMANTIC_HEX.success, sub: "Payments received" },
          { title: "Outstanding", value: totalOutstanding, color: SEMANTIC_HEX.warning, sub: `${stats?.sent ?? 0} awaiting` },
          { title: "Overdue", value: totalOverdue, color: SEMANTIC_HEX.danger, sub: `${stats?.overdue ?? 0} overdue` },
        ].map(({ title, value, color, sub }) => (
          <Col xs={12} xl={6} key={title}>
            <Card size="small" className="rounded-xl border-slate-100 shadow-sm">
              <Statistic
                title={title}
                value={value}
                precision={2}
                prefix="£"
                valueStyle={{ color, fontSize: 20 }}
              />
              <div className="text-xs text-slate-400 mt-0.5">{sub}</div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Tabs + Filters */}
      <div className="mb-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
            {TABS.map((tab) => {
              const count = tab.key === "all" ? stats?.all : (stats as any)?.[tab.key];
              return (
                <button
                  key={tab.key}
                  onClick={() => { setActiveTab(tab.key); setPage(1); }}
                  className={`text-sm font-medium pb-3 px-3 flex items-center gap-1.5 transition-colors whitespace-nowrap border-b-2 ${
                    activeTab === tab.key
                      ? "border-primary text-primary"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {tab.label}
                  {count != null && count > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Input
            prefix={<Search size={14} className="text-slate-400" />}
            placeholder="Search invoice #, client, title…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="rounded-lg"
            style={{ maxWidth: 260 }}
            allowClear
          />
          <Select
            placeholder="Filter by type"
            value={invoiceTypeFilter || undefined}
            onChange={(v) => { setInvoiceTypeFilter(v || ""); setPage(1); }}
            allowClear
            style={{ width: 150 }}
            options={[
              { value: "standard", label: "Standard" },
              { value: "credit_note", label: "Credit Note" },
              { value: "pro_forma", label: "Pro Forma" },
              { value: "recurring", label: "Recurring" },
            ]}
          />
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Switch
              size="small"
              checked={overdueOnly}
              onChange={(v) => { setOverdueOnly(v); setPage(1); }}
            />
            <span>Overdue only</span>
          </div>
        </div>
      </div>

      {/* Table */}
      {isError && (
        <div className="mb-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load data"
            description={(error as any)?.message ?? "Please refresh the page or contact support."}
          />
        </div>
      )}
      <Card
        className="rounded-2xl border border-slate-200 shadow-sm"
        styles={{ body: { padding: 0 } }}
      >
        <Table
          dataSource={invoices}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: 20,
            total,
            showSizeChanger: false,
            showTotal: (t) => `${t} invoices`,
            onChange: (p) => setPage(p),
          }}
          scroll={{ x: 900 }}
        />
      </Card>

      {/* Modals */}
      <InvoiceFormModal
        open={showCreate}
        editInvoice={null}
        onClose={() => setShowCreate(false)}
        onSuccess={() => {
          setShowCreate(false);
          refetchWithQuery({
            ...queryData,
            page: 1,
          });
          refetchStats();
        }}
      />

      {editInvoice && (
        <InvoiceFormModal
          open={!!editInvoice}
          editInvoice={editInvoice}
          onClose={() => setEditInvoice(null)}
          onSuccess={() => {
            setEditInvoice(null);
            refetchWithQuery(queryData);
          }}
        />
      )}

      <FromPOModal
        open={showFromPO}
        onClose={() => setShowFromPO(false)}
        onSuccess={() => {
          setShowFromPO(false);
          refetchWithQuery({ ...queryData, page: 1 });
        }}
      />

      <FromQuoteModal
        open={showFromQuote}
        onClose={() => setShowFromQuote(false)}
        onSuccess={() => {
          setShowFromQuote(false);
          refetchWithQuery({
            ...queryData,
            page: 1,
          });
          refetchStats();
        }}
      />

      <PaymentModal
        invoice={paymentInvoice}
        onClose={() => setPaymentInvoice(null)}
        onSuccess={() => {
          setPaymentInvoice(null);
          refetchWithQuery(queryData);
          refetchStats();
        }}
      />

      <StatusModal
        invoice={statusInvoice}
        onClose={() => setStatusInvoice(null)}
        onSuccess={() => {
          setStatusInvoice(null);
          refetchWithQuery(queryData);
          refetchStats();
        }}
      />

      <InvoiceDetailDrawer
        invoice={detailInvoice}
        onClose={() => setDetailInvoice(null)}
        onDownloadPdf={handleDownloadPdf}
      />

      {historyInvoice && (
        <StatusHistoryModal
          open={!!historyInvoice}
          entityType="invoice"
          entityId={historyInvoice.id}
          entityLabel={historyInvoice.invoice_number}
          onClose={() => setHistoryInvoice(null)}
        />
      )}

      <CreditNoteModal
        invoice={creditNoteInvoice}
        onClose={() => setCreditNoteInvoice(null)}
        onSuccess={() => {
          setCreditNoteInvoice(null);
          refetchWithQuery({ ...queryData, page: 1 });
          refetchStats();
        }}
      />
    </div>
  );
};

export default InvoicesPage;
